import { AppError } from '../domain/errors'
import {
  cleanText,
  isValidEmail,
  isValidUsername,
  normalizeEmail,
  normalizeUserProfile,
  normalizeUsername,
  profileUsername,
} from '../domain/helpers'
import type { UserProfile } from '../domain/models'
import type {
  AuthenticatedAccount,
  AuthPort,
  ClockPort,
  IdPort,
  PasswordHasherPort,
  StoragePort,
} from '../domain/ports'
import { defaultShowId } from '../fixtures/tour'
import { WebCryptoPasswordHasher } from './passwordHasher'
import { systemClock, systemIds } from './runtime'

export const AUTH_CREDENTIAL_STORAGE_KEY = 'orbral.paradox.auth.credentials.v1'

interface CredentialRecord {
  user: UserProfile
  emailNormalized: string
  passwordSalt: string
  passwordDigest: string
}

interface CredentialVaultV1 {
  version: 1
  records: CredentialRecord[]
}

const emptyVault = (): CredentialVaultV1 => ({ version: 1, records: [] })

const isUserProfile = (value: unknown): value is UserProfile => {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<UserProfile>
  return typeof candidate.id === 'string'
    && typeof candidate.displayName === 'string'
    && typeof candidate.email === 'string'
    && typeof candidate.homeShowId === 'string'
    && typeof candidate.createdAt === 'string'
}

const isVault = (value: unknown): value is CredentialVaultV1 => {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<CredentialVaultV1>
  return candidate.version === 1
    && Array.isArray(candidate.records)
    && candidate.records.every((record) => Boolean(
      record
      && typeof record === 'object'
      && isUserProfile((record as Partial<CredentialRecord>).user)
      && typeof (record as Partial<CredentialRecord>).emailNormalized === 'string'
      && typeof (record as Partial<CredentialRecord>).passwordSalt === 'string'
      && typeof (record as Partial<CredentialRecord>).passwordDigest === 'string',
    ))
}

const sessionLifetime = (remember: boolean): number => remember
  ? 30 * 24 * 60 * 60 * 1_000
  : 8 * 60 * 60 * 1_000

export class LocalAuthAdapter implements AuthPort {
  private readonly storage: StoragePort
  private readonly hasher: PasswordHasherPort
  private readonly clock: ClockPort
  private readonly ids: IdPort

  constructor(options: {
    storage: StoragePort
    hasher?: PasswordHasherPort
    clock?: ClockPort
    ids?: IdPort
  }) {
    this.storage = options.storage
    this.hasher = options.hasher ?? new WebCryptoPasswordHasher()
    this.clock = options.clock ?? systemClock
    this.ids = options.ids ?? systemIds
  }

  private readVault(): CredentialVaultV1 {
    try {
      const raw = this.storage.getItem(AUTH_CREDENTIAL_STORAGE_KEY)
      if (!raw) return emptyVault()
      const parsed: unknown = JSON.parse(raw)
      if (!isVault(parsed)) return emptyVault()
      return {
        ...parsed,
        records: parsed.records.map((record) => ({
          ...record,
          user: normalizeUserProfile(record.user),
        })),
      }
    } catch {
      return emptyVault()
    }
  }

  private writeVault(vault: CredentialVaultV1): void {
    try {
      this.storage.setItem(AUTH_CREDENTIAL_STORAGE_KEY, JSON.stringify(vault))
    } catch {
      throw new AppError('storage_unavailable', 'This browser could not save the account securely.', 'unavailable')
    }
  }

  private createSession(userId: string, remember: boolean) {
    const issuedAt = this.clock.now()
    return {
      id: this.ids.create('session'),
      userId,
      issuedAt: issuedAt.toISOString(),
      expiresAt: new Date(issuedAt.getTime() + sessionLifetime(remember)).toISOString(),
      remember,
    }
  }

  async register(input: Parameters<AuthPort['register']>[0]): Promise<AuthenticatedAccount> {
    const realName = cleanText(input.realName ?? input.displayName ?? '')
    const rawUsername = input.username ?? (input.displayName ?? '').replace(/[^A-Za-z0-9._]/g, '')
    const username = normalizeUsername(rawUsername)
    const emailNormalized = normalizeEmail(input.email)
    if (realName.length < 2 || !isValidUsername(username) || !isValidEmail(emailNormalized) || input.password.length < 8 || !input.consent) {
      throw new AppError('auth_invalid', 'Complete every account field to continue.')
    }

    const vault = this.readVault()
    if (vault.records.some((record) => record.emailNormalized === emailNormalized)) {
      throw new AppError('auth_duplicate', 'An account already uses that email.')
    }
    if (vault.records.some((record) => profileUsername(record.user).toLowerCase() === username.toLowerCase())) {
      throw new AppError('auth_duplicate_username', 'That username is already saved on this device.')
    }

    const passwordHash = await this.hasher.hash(input.password)
    const createdAt = this.clock.now().toISOString()
    const user: UserProfile = {
      id: this.ids.create('fan'),
      displayName: realName,
      realName,
      username,
      email: emailNormalized,
      homeShowId: defaultShowId,
      createdAt,
    }
    vault.records.push({
      user,
      emailNormalized,
      passwordSalt: passwordHash.salt,
      passwordDigest: passwordHash.digest,
    })
    this.writeVault(vault)
    return { user, session: this.createSession(user.id, true) }
  }

  async login(input: Parameters<AuthPort['login']>[0]): Promise<AuthenticatedAccount> {
    const emailNormalized = normalizeEmail(input.email)
    const record = this.readVault().records.find((candidate) => candidate.emailNormalized === emailNormalized)
    if (!record || !await this.hasher.verify(input.password, {
      salt: record.passwordSalt,
      digest: record.passwordDigest,
    })) {
      throw new AppError('auth_invalid', 'Email or password did not match.')
    }
    const user = normalizeUserProfile(record.user)
    return { user, session: this.createSession(user.id, input.remember) }
  }

  async recover(email: string): Promise<void> {
    if (!isValidEmail(email)) throw new AppError('auth_invalid', 'Enter a valid email address.')
    // Intentionally generic: local recovery never reveals whether an email exists.
    await Promise.resolve()
  }

  updateProfile(user: UserProfile): void {
    const vault = this.readVault()
    const index = vault.records.findIndex((record) => record.user.id === user.id)
    if (index < 0) return
    const record = vault.records[index]
    if (!record) return
    vault.records[index] = { ...record, user: normalizeUserProfile(user) }
    this.writeVault(vault)
  }

  deleteAccount(userId: string): void {
    const vault = this.readVault()
    this.writeVault({ version: 1, records: vault.records.filter((record) => record.user.id !== userId) })
  }
}
